import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../../components/ui";
import { listCards } from "../../../api/cards";
import { listSalesOrders, getSalesOrder } from "../../../api/salesOrders";
import { itemVariantSummary, fmtItemQty } from "../../../utils/salesOrderItems";
import { listBaseRawMaterials, receiveRawMaterial } from "../../../api/rawMaterials";
import { getMaterialVariantDimensions } from "../../../api/materials";
import MaterialVariantSelectModal from "../../../components/MaterialVariantSelectModal";

const CUSTOMERS_PER_DROPDOWN_PAGE = 25;

export default function BoxIntakePage() {
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [baseMaterials, setBaseMaterials] = useState([]);
  const [baseLoading, setBaseLoading] = useState(false);

  const [boxKind, setBoxKind] = useState(""); // inner | outer
  const [boxCode, setBoxCode] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [templateDimensions, setTemplateDimensions] = useState([]);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [variant, setVariant] = useState(null);

  const [forCustomerCardId, setForCustomerCardId] = useState("");
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerDropdownPage, setCustomerDropdownPage] = useState(0);
  const [customerDropdownAnchor, setCustomerDropdownAnchor] = useState(null);
  const [forSalesOrderId, setForSalesOrderId] = useState("");
  const [forSalesOrderItemId, setForSalesOrderItemId] = useState("");

  const [orderSearch, setOrderSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const orderReq = useRef(0);

  const [orderItems, setOrderItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState("");

  const [supplierCardId, setSupplierCardId] = useState("");
  const [receivedByEmployeeCardId, setReceivedByEmployeeCardId] = useState("");
  const [inboundDocNo, setInboundDocNo] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [priceUnit, setPriceUnit] = useState("");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listCards({ type: "supplier" })
      .then(setSuppliers)
      .catch((err) => {
        console.error("SUPPLIERS LOAD ERROR", err);
        setSuppliers([]);
      });
  }, []);

  useEffect(() => {
    listCards({ type: "employee" })
      .then(setEmployees)
      .catch((err) => {
        console.error("EMPLOYEES LOAD ERROR", err);
        setEmployees([]);
      });
  }, []);

  useEffect(() => {
    listCards({ type: "customer", limit: 500 })
      .then(setCustomers)
      .catch((err) => {
        console.error("CUSTOMERS LOAD ERROR", err);
        setCustomers([]);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadBase() {
      setBaseLoading(true);
      try {
        const items = await listBaseRawMaterials("box");
        if (!cancelled) setBaseMaterials(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error("BASE BOX MATERIALS LOAD ERROR", e);
        if (!cancelled) setBaseMaterials([]);
      } finally {
        if (!cancelled) setBaseLoading(false);
      }
    }
    loadBase();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadTemplate() {
      try {
        if (!materialId) {
          setTemplateDimensions([]);
          setVariant(null);
          return;
        }
        const items = await getMaterialVariantDimensions(Number(materialId));
        if (!cancelled) setTemplateDimensions(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error("BOX TEMPLATE LOAD ERROR", e);
        if (!cancelled) setTemplateDimensions([]);
      }
    }
    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [materialId]);

  // Koli tipi ile stok kartı 1:1 eşleşir (150.06.01 = iç, 150.06.02 = dış),
  // bu yüzden kart ayrıca seçtirilmez; koli tipinden otomatik belirlenir.
  const materialForKind = useMemo(() => {
    const items = Array.isArray(baseMaterials) ? baseMaterials : [];
    if (!boxKind) return null;
    const suffix = boxKind === "inner" ? ".01" : ".02";
    const nameHint = boxKind === "inner" ? "iç" : "dış";
    return (
      items.find((m) => String(m.code || "").trim().endsWith(suffix)) ||
      items.find((m) => String(m.name || "").toLocaleLowerCase("tr").includes(nameHint)) ||
      null
    );
  }, [baseMaterials, boxKind]);

  const selectedMaterial = useMemo(
    () => (materialId ? baseMaterials.find((m) => String(m.id) === String(materialId)) : null),
    [baseMaterials, materialId]
  );

  useEffect(() => {
    const nextId = materialForKind ? String(materialForKind.id) : "";
    setMaterialId((prev) => {
      if (prev === nextId) return prev;
      setVariant(null);
      return nextId;
    });
  }, [materialForKind]);

  useEffect(() => {
    let cancelled = false;
    const reqId = ++orderReq.current;
    async function loadOrders() {
      setOrdersError("");
      setOrders([]);
      setForSalesOrderId("");
      setForSalesOrderItemId("");
      setOrderItems([]);
      setItemsError("");

      const custId = forCustomerCardId ? Number(forCustomerCardId) : null;
      if (!custId || !Number.isFinite(custId)) return;

      const q = String(orderSearch || "").trim();
      setOrdersLoading(true);
      try {
        const items = await listSalesOrders({ status: "open", search: q || undefined, limit: 100, page: 1 });
        if (cancelled) return;
        if (orderReq.current !== reqId) return;
        const rows = Array.isArray(items) ? items : [];
        const filtered = rows.filter((o) => Number(o.customer_card_id) === Number(custId));
        setOrders(filtered);
      } catch (err) {
        if (cancelled) return;
        if (orderReq.current !== reqId) return;
        console.error("BOX SALES ORDERS LOAD ERROR", err);
        setOrders([]);
        setOrdersError(err?.response?.data?.error?.message || "Siparişler yüklenemedi");
      } finally {
        if (!cancelled && orderReq.current === reqId) setOrdersLoading(false);
      }
    }

    const t = setTimeout(loadOrders, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [forCustomerCardId, orderSearch]);

  useEffect(() => {
    let cancelled = false;
    async function loadItems() {
      setItemsError("");
      setOrderItems([]);
      setForSalesOrderItemId("");

      const soId = forSalesOrderId ? Number(forSalesOrderId) : null;
      if (!soId || !Number.isFinite(soId)) return;

      setItemsLoading(true);
      try {
        const d = await getSalesOrder(Number(soId));
        if (cancelled) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setOrderItems(items.filter((it) => String(it.status || "active") === "active"));
      } catch (err) {
        if (cancelled) return;
        console.error("BOX SALES ORDER ITEMS LOAD ERROR", err);
        setOrderItems([]);
        setItemsError(err?.response?.data?.error?.message || "Sipariş kalemleri yüklenemedi");
      } finally {
        if (!cancelled) setItemsLoading(false);
      }
    }
    loadItems();
    return () => {
      cancelled = true;
    };
  }, [forSalesOrderId]);

  async function onSave() {
    const q = Number(qty);
    const hasTemplate = templateDimensions.length > 0;

    if (!materialId) {
      alert("Koli stok kartı seçin");
      return;
    }
    if (hasTemplate && !(variant?.selections || []).length) {
      alert("Varyant seçin");
      return;
    }
    if (!receivedByEmployeeCardId) {
      alert("Teslim alan seçin");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("Miktar pozitif olmalı");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        material_id: Number(materialId),
        selections: variant?.selections || [],
        box_code: boxCode.trim() ? boxCode.trim() : null,
        for_customer_card_id: forCustomerCardId ? Number(forCustomerCardId) : null,
        for_sales_order_id: forSalesOrderId ? Number(forSalesOrderId) : null,
        for_sales_order_item_id: forSalesOrderItemId ? Number(forSalesOrderItemId) : null,
        supplier_card_id: supplierCardId ? Number(supplierCardId) : null,
        inbound_doc_no: inboundDocNo.trim() ? inboundDocNo.trim() : null,
        received_by_employee_card_id: receivedByEmployeeCardId ? Number(receivedByEmployeeCardId) : null,
        qty: q,
        note: note.trim() ? note.trim() : null,
        unit_cost: unitCost !== "" ? Number(unitCost) : null,
        currency: currency || "TRY",
        price_unit: priceUnit || null,
      };
      const d = await receiveRawMaterial("box", payload);
      alert(`Kaydedildi.\n\nLot: ${d.lot_no}\nBirim: ${d.unit || "-"}`);
      setBoxCode("");
      setQty("");
      setInboundDocNo("");
      setNote("");
      setUnitCost("");
      setCurrency("TRY");
      setPriceUnit("");
    } catch (err) {
      console.error("BOX RECEIVE ERROR", err);
      alert(err?.response?.data?.error?.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1200, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
      <PageHeader
        title="Hammadde Giriş — Koli"
        subtitle="İç/Dış koli seçin, stok kartını ve varyantları doldurun, sipariş bilgisini seçin."
      />
      {baseLoading ? <LinearProgress /> : null}

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
          Koli Bilgileri
        </Typography>

        <Stack spacing={2}>
          <FormControl fullWidth size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="box-kind-label">Koli Tipi</InputLabel>
            <Select
              labelId="box-kind-label"
              label="Koli Tipi"
              value={boxKind}
              onChange={(e) => {
                setBoxKind(String(e.target.value));
              }}
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              <MenuItem value="inner">İç Koli</MenuItem>
              <MenuItem value="outer">Dış Koli</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Koli Kodu (ops.)"
            value={boxCode}
            onChange={(e) => setBoxCode(e.target.value)}
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
            <Stack spacing={0.25}>
              <Typography sx={{ fontWeight: 900 }}>Varyant</Typography>
              <Typography variant="body2" color="text.secondary">
                {variant?.display_name
                  ? variant.display_name
                  : templateDimensions.length
                    ? "Seçilmedi (koli ölçüleri, tabak bilgileri ve paket adedi)"
                    : "Bu malzemede varyant yok"}
              </Typography>
            </Stack>
            <Button variant="outlined" disabled={!materialId || !templateDimensions.length} onClick={() => setVariantModalOpen(true)} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#818cf8", color: "#4338ca", "&:hover": { borderColor: "#4f46e5", bgcolor: "#eef2ff" } }}>
              Varyant Seç
            </Button>
          </Stack>

          {!boxKind ? (
            <Alert severity="info">Stok girişi için önce koli tipini seçin.</Alert>
          ) : !materialId ? (
            <Alert severity="warning">
              {boxKind === "inner" ? "İç" : "Dış"} koli stok kartı bulunamadı (150.06.
              {boxKind === "inner" ? "01" : "02"}).
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
          Sipariş Bilgisi (Takip)
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Box sx={{ minWidth: 320, flex: "1 1 320px" }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  color: "text.secondary",
                  fontSize: "0.75rem",
                }}
              >
                Müşteri (ops.)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  setCustomerDropdownAnchor(e.currentTarget);
                  setCustomerDropdownOpen(true);
                  setCustomerDropdownPage(0);
                }}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  textTransform: "none",
                  height: 40,
                  width: "100%",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                  "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)", bgcolor: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                {forCustomerCardId
                  ? (() => {
                      const c = customers.find((x) => String(x.id) === String(forCustomerCardId));
                      return c ? `${c.code ? `${c.code} - ` : ""}${c.name}` : "Seçiniz";
                    })()
                  : "Seçiniz"}
              </Button>
              <Popover
                open={customerDropdownOpen}
                anchorEl={customerDropdownAnchor}
                onClose={() => setCustomerDropdownOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 340 } } }}
              >
                <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                  <ListItemButton
                    selected={!forCustomerCardId}
                    onClick={() => {
                      setForCustomerCardId("");
                      setOrderSearch("");
                      setForSalesOrderId("");
                      setForSalesOrderItemId("");
                      setOrders([]);
                      setOrderItems([]);
                      setCustomerDropdownOpen(false);
                    }}
                  >
                    <ListItemText primary="Seçiniz" primaryTypographyProps={{ fontStyle: "italic" }} />
                  </ListItemButton>
                  {customers
                    .slice(
                      customerDropdownPage * CUSTOMERS_PER_DROPDOWN_PAGE,
                      (customerDropdownPage + 1) * CUSTOMERS_PER_DROPDOWN_PAGE
                    )
                    .map((c) => (
                      <ListItemButton
                        key={c.id}
                        selected={String(forCustomerCardId) === String(c.id)}
                        onClick={() => {
                          setForCustomerCardId(String(c.id));
                          setOrderSearch("");
                          setForSalesOrderId("");
                          setForSalesOrderItemId("");
                          setOrders([]);
                          setOrderItems([]);
                          setCustomerDropdownOpen(false);
                        }}
                      >
                        <ListItemText primary={(c.code ? `${c.code} - ` : "") + c.name} />
                      </ListItemButton>
                    ))}
                </Box>
                {customers.length > CUSTOMERS_PER_DROPDOWN_PAGE ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 1,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Button
                      size="small"
                      disabled={customerDropdownPage === 0}
                      onClick={() => setCustomerDropdownPage((p) => p - 1)}
                    >
                      ▲ Önceki
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Sayfa {customerDropdownPage + 1} / {Math.ceil(customers.length / CUSTOMERS_PER_DROPDOWN_PAGE) || 1}
                    </Typography>
                    <Button
                      size="small"
                      disabled={customerDropdownPage >= Math.ceil(customers.length / CUSTOMERS_PER_DROPDOWN_PAGE) - 1}
                      onClick={() => setCustomerDropdownPage((p) => p + 1)}
                    >
                      ▼ Sonraki
                    </Button>
                  </Box>
                ) : null}
              </Popover>
            </Box>

            <TextField
              size="small"
              label="Sipariş Ara (ops.)"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              disabled={!forCustomerCardId}
              sx={{ minWidth: 260, flex: "1 1 260px" }}
            />

            <FormControl
              fullWidth
              size="small"
              disabled={!forCustomerCardId || ordersLoading}
              sx={{ minWidth: 260, flex: "1 1 260px" }}
            >
              <InputLabel id="so-label">Sipariş (ops.)</InputLabel>
              <Select
                labelId="so-label"
                label="Sipariş (ops.)"
                value={forSalesOrderId}
                onChange={(e) => {
                  setForSalesOrderId(String(e.target.value));
                }}
              >
                <MenuItem value="">
                  <em>Seçiniz</em>
                </MenuItem>
                {orders.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.order_no}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {ordersError ? <Alert severity="warning">{ordersError}</Alert> : null}

          <FormControl fullWidth size="small" disabled={!forSalesOrderId || itemsLoading} sx={{ minWidth: 520 }}>
            <InputLabel id="soi-label">Sipariş Kalemi (ops.)</InputLabel>
            <Select
              labelId="soi-label"
              label="Sipariş Kalemi (ops.)"
              value={forSalesOrderItemId}
              onChange={(e) => setForSalesOrderItemId(String(e.target.value))}
              // Menüde iki satır gösteriliyor; kapalı haldeki alan tek satır kalsın.
              renderValue={(val) => {
                if (!val) return <em>Seçiniz</em>;
                const it = orderItems.find((x) => String(x.id) === String(val));
                if (!it) return "";
                const v = itemVariantSummary(it);
                return `#${it.line_no} — ${it.material_code || ""} — ${fmtItemQty(it.qty)}${v ? ` • ${v}` : ""}`;
              }}
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              {orderItems.map((it) => {
                const v = itemVariantSummary(it);
                return (
                  <MenuItem key={it.id} value={String(it.id)}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{it.line_no} — {it.material_code || ""} {it.material_name || ""} — {fmtItemQty(it.qty)}
                      </Typography>
                      {v ? (
                        <Typography variant="caption" color="text.secondary">
                          {v}
                        </Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {itemsError ? <Alert severity="warning">{itemsError}</Alert> : null}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
          Stok Girişi
        </Typography>

        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="supplier-label">Tedarikçi (ops.)</InputLabel>
            <Select
              labelId="supplier-label"
              label="Tedarikçi (ops.)"
              value={supplierCardId}
              onChange={(e) => setSupplierCardId(e.target.value)}
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {(s.code ? `${s.code} - ` : "") + s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField size="small" label="İrsaliye Numarası (ops.)" value={inboundDocNo} onChange={(e) => setInboundDocNo(e.target.value)} />

          <FormControl fullWidth size="small" required>
            <InputLabel id="received-by-label">Teslim Alan</InputLabel>
            <Select
              labelId="received-by-label"
              label="Teslim Alan"
              value={receivedByEmployeeCardId}
              onChange={(e) => setReceivedByEmployeeCardId(e.target.value)}
            >
              <MenuItem value="">
                <em>Seçiniz</em>
              </MenuItem>
              {employees.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {(c.code ? `${c.code} - ` : "") + c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField size="small" label={`Miktar (${selectedMaterial?.unit || "—"})`} value={qty} onChange={(e) => setQty(e.target.value)} />
          <TextField size="small" label="Not (ops.)" value={note} onChange={(e) => setNote(e.target.value)} />

          <Stack direction="row" spacing={1}>
            <TextField size="small" label="Birim Fiyat" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} inputMode="decimal" sx={{ flex: 2 }} />
            <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
              <InputLabel>Fiyatın Birimi</InputLabel>
              <Select value={priceUnit} label="Fiyatın Birimi" onChange={(e) => setPriceUnit(e.target.value)}>
                <MenuItem value=""><em>—</em></MenuItem>
                <MenuItem value="kg">kg</MenuItem>
                <MenuItem value="adet">adet</MenuItem>
                <MenuItem value="m">m</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
              <InputLabel>Döviz</InputLabel>
              <Select value={currency} label="Döviz" onChange={(e) => setCurrency(e.target.value)}>
                <MenuItem value="TRY">TRY</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Button variant="contained" fullWidth disabled={saving || baseLoading} onClick={onSave} sx={{ mt: 0.5, py: 1.2, borderRadius: 2.25, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Stack>
      </Paper>

      {variantModalOpen ? (
        <MaterialVariantSelectModal
          materialId={Number(materialId)}
          materialName={selectedMaterial?.name || ""}
          templateDimensions={templateDimensions}
          onClose={() => setVariantModalOpen(false)}
          onResolved={(v) => setVariant(v)}
        />
      ) : null}
    </Stack>
  );
}
